import React from 'react';
import Header from '../components/Header';
import HeroSlider from '../components/HeroSlider';
import MarqueeTicker from '../components/MarqueeTicker';
import FruitIcons from '../components/FruitIcons';
import DirectorDesk from '../components/DirectorDesk';
import VarietiesCarousel from '../components/VarietiesCarousel';
import TechnologiesList from '../components/TechnologiesList';
import KeyImpacts from '../components/KeyImpacts';
import KeyResearchGrid from '../components/KeyResearchGrid';
import KeyOfferings from '../components/KeyOfferings';
import UsefulLinks from '../components/UsefulLinks';
import NewsEvents from '../components/NewsEvents';
import SocialFeeds from '../components/SocialFeeds';
import Footer from '../components/Footer';
import { useHomeData } from '../hooks/useHomeData';

export default function HomePage() {
  const { data, loading, error } = useHomeData();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0E6D2B] border-t-transparent mx-auto mb-3"></div>
          <p className="text-sm text-[#6B6B6B]">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center text-red-600">
          <p className="text-sm">Error loading data: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <main id="main-content" className="flex-1 overflow-x-hidden">
        {/* Hero Banner Slider */}
        <HeroSlider banners={data?.banners || []} />

        {/* Marquee Announcements */}
        <MarqueeTicker announcements={data?.marquee_announcements || []} />

        {/* Fruit Icons Section */}
        <FruitIcons />

        {/* Director's Desk */}
        <DirectorDesk director={data?.director || {}} />

        {/* Two Column: Varieties + Technologies - Desktop Only */}
        <section className="py-4 md:py-8 bg-white hidden lg:block">
          <div className="container-cish">
            <div className="grid lg:grid-cols-2 gap-4 md:gap-8 items-stretch">
              <div className="flex flex-col">
                <VarietiesCarousel varieties={data?.varieties || []} />
              </div>
              <div className="flex flex-col">
                <TechnologiesList technologies={data?.technologies || []} />
              </div>
            </div>
          </div>
        </section>
        
        {/* Mobile: Stacked Sections */}
        <div className="lg:hidden">
          {/* Varieties Section - Mobile */}
          <section className="py-4 bg-white">
            <div className="container-cish">
              <VarietiesCarousel varieties={data?.varieties || []} />
            </div>
          </section>

          {/* Technologies Section - Mobile */}
          <section className="py-4 bg-white">
            <div className="container-cish">
              <TechnologiesList technologies={data?.technologies || []} />
            </div>
          </section>
        </div>

        {/* Key Impacts */}
        <KeyImpacts />

        {/* Key Research - New Redesigned Grid */}
        <KeyResearchGrid />

        {/* Two Column: Key Offerings + Useful Links - Side by Side with fixed heights */}
        <section className="py-6 md:py-10 bg-white overflow-hidden">
          <div className="container-cish">
            <div className="grid lg:grid-cols-[1fr_300px] gap-6 items-start">
              {/* Key Offerings - Left Side */}
              <KeyOfferings
                announcements={data?.all_announcements || []}
                jobs={data?.jobs || []}
                tenders={data?.tenders || []}
              />
              {/* Useful Links - Right Side, same height as Key Offerings */}
              <div className="hidden lg:block">
                <UsefulLinks />
              </div>
            </div>
            {/* Mobile - Useful Links below */}
            <div className="lg:hidden mt-6">
              <UsefulLinks />
            </div>
          </div>
        </section>

        {/* News & Events */}
        <NewsEvents newsEvents={data?.news_events || []} />

        {/* Social Media Feeds */}
        <SocialFeeds socialFeeds={data?.social_feeds || {}} />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
